/* eslint-disable react/prop-types */
import Vimeo from "@u-wave/react-vimeo";

import React, { useState } from "react";

import { motion, AnimatePresence } from "framer-motion";
import styled, { keyframes } from "styled-components";

import Markdown from "../../styles/Markdown";

const StyledTextWrapper = styled.div`
  position: relative;
  width: 100%;
  padding: ${(props) => props.itemPadding || "0 15px"};
  margin-bottom: ${(props) => props.itemMarginBottom || "30px"};
  ${(props) => props.dynamicStyles}
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
`;

const VideoPlayerContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  transition: opacity 300ms ease-in-out;
  /* Only make the container visible on hover */
  opacity: ${(props) => (props.isHovering ? 1 : 0)};
  pointer-events: none;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f0f0f0; /* 👈 Add this for the loader background */
`;

const VimeoWrapper = styled.div`
  width: 100%;
  height: 100%;
  transition: opacity 300ms ease-in-out;
  opacity: ${(props) => (props.isReady ? 1 : 0)};

  > div,
  iframe {
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    min-width: 100% !important;
    min-height: 100% !important;
    width: auto !important;
    height: auto !important;
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;
const LoadingSpinner = styled.div`
  position: absolute;
  z-index: 1;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 4px solid #fff;
  width: 40px;
  height: 40px;
  animation: ${spin} 1s linear infinite;
`;

const TextOverlay = styled(motion.div)`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  z-index: 2;
  color: white;
`;

function TextItemWithPreview({ item, itemPadding, itemMarginBottom }) {
  const { text, hoverText, CSS: textCSS, vimeoId } = item;
  const [isHovering, setIsHovering] = useState(false);
  const [isPlayerReady, setIsPlayerReady] = useState(false); // 👈 1. New state

  const overlaySlideVariants = {
    hidden: { y: "100%" },
    visible: { y: 0 },
  };

  return (
    <StyledTextWrapper
      itemPadding={itemPadding}
      itemMarginBottom={itemMarginBottom}
      dynamicStyles={textCSS}
      id={item.name}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <motion.div
        animate={{ opacity: isHovering && hoverText ? 0 : 1 }}
        transition={{ duration: 0.2 }}
      >
        <Markdown className="w-100 h-100 markup-container">{text}</Markdown>
      </motion.div>

      {vimeoId && (
        <VideoPlayerContainer
          isHovering={isHovering}
          className="vimeo-player-container"
        >
          {!isPlayerReady && <LoadingSpinner />}
          <VimeoWrapper isReady={isPlayerReady}>
            <Vimeo
              video={vimeoId}
              paused={!isHovering}
              onReady={() => setIsPlayerReady(true)} // Set state when player is loaded
              autoplay
              muted
              loop
              controls={false}
              background
            />
          </VimeoWrapper>
        </VideoPlayerContainer>
      )}
      <AnimatePresence>
        {isHovering && hoverText && (
          <TextOverlay
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="hover-text-container"
            variants={overlaySlideVariants}
            transition={{ duration: 0.4, ease: "easeInOut" }}
          >
            <Markdown className="w-100 h-100">{hoverText}</Markdown>
          </TextOverlay>
        )}
      </AnimatePresence>
    </StyledTextWrapper>
  );
}

export default TextItemWithPreview;
